import { type ReactNode, useMemo, useState } from "react";
import { AtcMainAreaLayout } from "~/features/addToCart/components/AddToCartProcess/shell/AtcContentLayout";

import { useFormContext } from "react-hook-form";
import type { AddToCartFormDraft } from "~/features/addToCart/hooks/useAddToCartForm";
import { useAddToCartDerived } from "~/features/addToCart/hooks/AddToCartDerivedContext";
import OptimeSolutionsMessage from "~/domains/optime/components/OptimeSolutionsMessage";
import { getCountriesListFromFifteendigit, isProductPMA } from "~/domains/product/utils/productDescriptorUtils";
import DisclaimerCountryOrigin from "~/domains/product/components/DisclaimerCountryOrigin";
import { type HousingProduct, HousingProductType, isKITProduct } from "~/domains/product/types/ProductData";
import classNames from "classnames";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import { Trans, useTranslation } from "react-i18next";
import { Translation } from "~/app/i18n/Translation";
import { HousingMessages } from "~/domains/housing/components/HousingMessages";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { type ColumnDef } from "@tanstack/react-table";
import { TanStackTable } from "~/common/components/Table/TanStackTable";
import { InfoProductMessagePMA } from "~/features/productDetails/components/ProductSummary/InfoProductMessagePMA";
import { LinkButton } from "~/common/components/Link";
import { Stack, Typography, styled } from "~/common/mui";
import { isNonNull } from "~/common/utils/reducers";
import { Alert } from "~/common/mui/Alert/Alert";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import AtcProductPriceInfo from "~/features/addToCart/components/v2/AtcProductPriceInfo/AtcProductPriceInfo";

interface Props {
  canSeeAddToCartDisclaimer: boolean;
  canSubmitToCartOrQuote: boolean;
  productDescriptor: ProductDescriptor;
}

export const KITSummaryScreen = ({ canSeeAddToCartDisclaimer, productDescriptor }: Props) => {
  const { t } = useTranslation();

  const formProps = useFormContext<AddToCartFormDraft>();
  const { main } = useAddToCartDerived();
  const watch = formProps.watch;
  const setValue = formProps.setValue;

  const quantity = watch("quantity");
  const priceReference = watch("priceReference");

  const optionalPartsForm = watch("optionalParts");

  if (!isKITProduct(productDescriptor.base)) {
    return null;
  }

  const { includedParts, optionalParts } = productDescriptor.base;
  const hasOptionalParts = Object.values(optionalPartsForm).some((part) => part.selected);
  const hasIncludedParts = !!includedParts.length;

  const selectedPack = main.selectedPack;
  const packSize = main.selectedPackaging?.packSize ?? selectedPack?.packSize;
  const isPackageSizeInvalid = quantity === 0 || (packSize != null && !Number.isInteger(quantity / packSize));

  return (
    <AtcMainAreaLayout
      packagingSlot={
        <Stack gap={2}>
          {hasIncludedParts && (
            <IncludedPartsTable
              title={t("modal.atc.housing.included.title")}
              items={includedParts}
              kitQuantity={quantity}
            />
          )}
          {hasOptionalParts && (
            <OptionalPartsTable
              className={classNames(hasIncludedParts && "scha__atc-popup--optional-parts")}
              title={t("modal.atc.housing.optional.title")}
              items={optionalParts}
            />
          )}
        </Stack>
      }
      pricingSlot={
        <AtcProductPriceInfo
          quantity={quantity}
          onChange={(newQty) => setValue("quantity", newQty, { shouldDirty: true, shouldTouch: true })}
          invalidQuantity={isPackageSizeInvalid ?? undefined}
          priceReference={priceReference}
          onPriceReferenceChange={(newVal) => setValue("priceReference", newVal ?? "")}
          descriptor={productDescriptor}
          showInfoOrderHandlingUnitQty={true}
        />
      }
      notificationsSlot={
        <Stack gap={1}>
          <HousingMessages.EditQuantity className="w-100" />
          {isProductPMA(productDescriptor) && <InfoProductMessagePMA />}
          {canSeeAddToCartDisclaimer && (
            <Alert severity="info" className="w-100">
              {t("addToCartPopup.disclaimer")}
            </Alert>
          )}
          {productDescriptor.base.isOptime && <OptimeSolutionsMessage />}
          {getCountriesListFromFifteendigit({ descriptor: productDescriptor }).length > 0 && (
            <DisclaimerCountryOrigin />
          )}
        </Stack>
      }
    />
  );
};

interface PartsTableProps {
  className?: string;
  title: ReactNode;
  items: HousingProduct[];
}

const IncludedPartsTable = ({
  className,
  title,
  items: parts,
  kitQuantity,
}: PartsTableProps & { kitQuantity: number }) => {
  const [displayAll, setDisplayAll] = useState(false);
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const items: DataTableRow[] = useMemo(
    () =>
      parts.map((item) => ({
        quantity: item.quantity * kitQuantity,
        description: getDescription(item),
        productId: getProductId(item),
      })),
    [parts, kitQuantity],
  );

  return (
    <PartsTableRoot className={classNames("scha__atc__parts-table", className)}>
      <PartsTableTitle>{title}</PartsTableTitle>
      <TanStackTable
        data={displayAll || isDesktop ? items : items.slice(0, 2)}
        columns={colConfig}
        fullwidth={true}
        variant={"clear"}
        className="scha__atc__kit_table"
      />
      {!displayAll && !isDesktop && (
        <LinkButton className={"mt-2"} onClick={() => setDisplayAll(true)}>
          {t("addToCartPopup.kit.expandAll.btn")}
        </LinkButton>
      )}
    </PartsTableRoot>
  );
};

const OptionalPartsTable = ({ className, title, items: parts }: PartsTableProps) => {
  const [displayAll, setDisplayAll] = useState(false);
  const { t } = useTranslation();
  const isDesktop = useIsMinDesktopSize();

  const formProps = useFormContext<AddToCartFormDraft>();
  const formItems = formProps.watch("optionalParts");

  const items = useMemo(
    () =>
      parts
        .map((part) => {
          const formItem = formItems[part.id];
          if (!formItem?.selected) {
            return null;
          }
          return {
            quantity: formItem.quantity,
            description: getDescription(part),
            productId: getProductId(part),
          };
        })
        .filter(isNonNull),
    [parts, formItems],
  );

  return (
    <PartsTableRoot className={classNames("scha__atc__parts-table", className)}>
      <PartsTableTitle>{title}</PartsTableTitle>
      <TanStackTable
        data={displayAll || isDesktop ? items : items.slice(0, 2)}
        columns={colConfig}
        fullwidth={true}
        variant={"clear"}
        className="scha__atc__kit_table"
      />
      {!displayAll && !isDesktop && (
        <LinkButton className={"mt-2"} onClick={() => setDisplayAll(true)}>
          {t("addToCartPopup.kit.expandAll.btn")}
        </LinkButton>
      )}
    </PartsTableRoot>
  );
};

const PartsTableRoot = styled("div")(({ theme }) => ({
  "&.scha__atc-popup--optional-parts": {
    marginTop: theme.spacing(1),
  },
  "& .scha__atc__kit_table > div": {
    borderRadius: theme.spacing(1.5),
  },
}));

interface DataTableRow {
  quantity: number;
  productId: JSX.Element;
  description: JSX.Element | string;
}

const colConfig: ColumnDef<DataTableRow, number>[] = [
  {
    accessorKey: "quantity",
    header: () => <Trans i18nKey={"input.quantity"} />,
    size: 0.2,
    cell: (cell) => cell.renderValue(),
  },
  {
    accessorKey: "productId",
    header: () => <Trans i18nKey={"bulkUpload.table.header.productId"} />,
    size: 0.4,
    cell: (cell) => cell.renderValue(),
  },
  {
    accessorKey: "description",
    header: () => <Trans i18nKey={"cartPage.report.description"} />,
    size: 0.4,
    cell: (cell) => cell.renderValue(),
  },
];

const getDescription = (item: HousingProduct) =>
  item.type === HousingProductType.HOUSING ? "-" : <Translation {...item.product.shortDescription} />;
const getProductId = (item: HousingProduct) => (
  <ProductDesignation
    designation={item.product.designation}
    isPreferred={item.product.preferred}
    reservePreferredSpace={false}
    size={"label-large"}
  />
);

const PartsTableTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.titleMedium,
  marginBottom: theme.spacing(2.5),
}));
